import React, { useEffect, useState, useRef, useCallback } from 'react'
import styles from './loan.module.scss'
import { isIOS, isAndroid, Storage } from '@bihu/common-js'
import { Toast } from 'antd-mobile'
import indexApi from '@/apis'
import { useSearchParams } from 'react-router-dom'
import { TOKEN } from '@/constants/storage'
import classNames from 'classnames'

interface Props {}

type AssetKey = 'housingFund' | 'socialSecurity' | 'houseProperty' | 'carProperty' | 'insurance'

const ASSET_OPTIONS: { key: AssetKey, label: string }[] = [
  { key: 'housingFund', label: '有公积金' },
  { key: 'socialSecurity', label: '有社保' },
  { key: 'houseProperty', label: '有房' },
  { key: 'carProperty', label: '有车' },
  { key: 'insurance', label: '有保险单' },
]

const HAS_VALUE = '2'
const NONE_VALUE = '0'

// 放款来源资产补充页
const FillInformationLoan: React.FC<Props> = () => {
  const [search] = useSearchParams()
  const [token, setToken] = useState('')
  const [first, setFirst] = useState(true)
  const [houseProperty, setHouseProperty] = useState('')
  const [carProperty, setCarProperty] = useState('')
  const [socialSecurity, setSocialSecurity] = useState('')
  const [housingFund, setHousingFund] = useState('')
  const [insurance, setInsurance] = useState('')
  const [realName, setRealName] = useState('')
  const [identity, setIdentity] = useState('')
  const [noneOfAbove, setNoneOfAbove] = useState(false)
  const [assetsExpanded, setAssetsExpanded] = useState(true)
  const [assetsDecided, setAssetsDecided] = useState(false)
  const [adChannelCode, setAdChannelCode] = useState('')
  const savingRef = useRef(false)


  const assetValues: Record<AssetKey, string> = {
    housingFund,
    socialSecurity,
    houseProperty,
    carProperty,
    insurance,
  }

  const setAssetValue = (key: AssetKey, value: string) => {
    switch (key) {
      case 'housingFund':
        setHousingFund(value)
        break
      case 'socialSecurity':
        setSocialSecurity(value)
        break
      case 'houseProperty':
        setHouseProperty(value)
        break
      case 'carProperty':
        setCarProperty(value)
        break
      case 'insurance':
        setInsurance(value)
        break
      default:
        break
    }
  }

  useEffect(() => {
    const code = search.get('adChannelCode')
    if (code) {
      setAdChannelCode(code)
      localStorage.setItem('ad_channel_code', code)
      localStorage.setItem('hostName', '卫付通')
    }
  }, [search])

  useEffect(() => {
    const urlToken = search.get('token')
    let tokenTemp = Storage.get(TOKEN)
    if (urlToken) {
      Storage.set(TOKEN, urlToken)
      setToken(urlToken)
      const url = window.location.href
      const updatedUrl = url.replace(/([?&])token=[^&]+(&|$)/, '$1')
      window.history.replaceState(null, 'null', updatedUrl)
    } else if (tokenTemp) {
      setToken(tokenTemp)
    }
  }, [search])

  useEffect(() => {
    async function getApplyStatus() {
      try {
        const res: any = await indexApi.getApplyStatus()
        indexApi.reportUserBehavior(1)
        let devicePlatForm: any = 3
        if (isAndroid) {
          devicePlatForm = 1
        }
        if (isIOS) {
          devicePlatForm = 2
        }
        indexApi.reportPhoneType(devicePlatForm)
        if (res.data) {
          fillData(res.data)
        }
        if (res.data && res.data?.submitStatus !== 0) {
          window.location.href = `/fill-information/waiting-result?sourceType=submit&isHalfProcess=true&options=${encodeURIComponent(JSON.stringify({ adChannelCode }))}`
        }
      } catch (error) {
        console.log('error', error)
      }
    }

    if (token) {
      getApplyStatus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const fillData = (init: any) => {
    if (init.realName) {
      setRealName(init.realName)
    }
    if (init.identity) {
      setIdentity(init.identity)
    }

    let hasAsset = false
    let allNone = true

    const applyAsset = (val: any, setter: (v: string) => void) => {
      if (val === null || val === undefined) {
        return
      }
      hasAsset = true
      if (val > 0) {
        setter(HAS_VALUE)
        allNone = false
      } else {
        setter(NONE_VALUE)
      }
    }

    applyAsset(init.houseProperty, setHouseProperty)
    applyAsset(init.carProperty, setCarProperty)
    applyAsset(init.socialSecurity, setSocialSecurity)
    applyAsset(init.housingFund, setHousingFund)
    applyAsset(init.insurance, setInsurance)

    if (hasAsset) {
      setAssetsDecided(true)
      setNoneOfAbove(allNone)
    }
  }

  const goNext = useCallback(() => {
    window.location.href = `/fill-information/waiting-result?sourceType=submit&isHalfProcess=true&options=${encodeURIComponent(JSON.stringify({ adChannelCode }))}`
  }, [adChannelCode])

  const save = async (params: {
    houseProperty: string,
    carProperty: string,
    socialSecurity: string,
    housingFund: string,
    insurance: string,
  }, shouldGoNext: boolean) => {
    if (savingRef.current) {
      return
    }
    savingRef.current = true
    setFirst(false)
    localStorage.setItem('fillInformation', JSON.stringify(params))
    try {
      await indexApi.tempSave(params)
      if (shouldGoNext) {
        goNext()
      }
    } finally {
      savingRef.current = false
    }
  }

  const buildAssetParams = (override?: Partial<Record<AssetKey, string>>, forceNone?: boolean) => {
    const isNone = forceNone ?? noneOfAbove
    if (isNone) {
      return {
        houseProperty: NONE_VALUE,
        carProperty: NONE_VALUE,
        socialSecurity: NONE_VALUE,
        housingFund: NONE_VALUE,
        insurance: NONE_VALUE,
      }
    }
    const current = { ...assetValues, ...override }
    return {
      houseProperty: current.houseProperty === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      carProperty: current.carProperty === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      socialSecurity: current.socialSecurity === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      housingFund: current.housingFund === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      insurance: current.insurance === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
    }
  }

  const trySave = (
    nextAssets: Partial<Record<AssetKey, string>>,
    decided: boolean,
    isNone: boolean,
    shouldGoNext = false,
  ) => {
    if (!decided) {
      return
    }
    save(buildAssetParams(nextAssets, isNone), shouldGoNext)
  }

  const hasSelectedAsset = (values: Record<AssetKey, string> = assetValues) => (
    Object.values(values).some(val => val === HAS_VALUE)
  )

  const handleAssetToggle = (key: AssetKey) => {
    const current = assetValues[key]
    const nextVal = current === HAS_VALUE ? '' : HAS_VALUE
    const nextAssets = { ...assetValues, [key]: nextVal }
    const decided = Object.values(nextAssets).some(val => val === HAS_VALUE)
    setAssetValue(key, nextVal)
    setNoneOfAbove(false)
    // 至少选一项才算有效；全部取消则需再选「以上都没有」
    setAssetsDecided(decided)
    if (decided) {
      trySave(nextAssets, true, false, false)
    }
  }

  const handleNoneOfAbove = () => {
    setNoneOfAbove(true)
    setAssetsDecided(true)
    setHouseProperty(NONE_VALUE)
    setCarProperty(NONE_VALUE)
    setSocialSecurity(NONE_VALUE)
    setHousingFund(NONE_VALUE)
    setInsurance(NONE_VALUE)
    trySave({}, true, true, false)
  }

  const handleNext = () => {
    if (!noneOfAbove && !hasSelectedAsset()) {
      Toast.show('请至少选择一项资产，都没有请选择「以上都没有」')
      return
    }
    trySave(assetValues, true, noneOfAbove, true)
  }

  useEffect(() => {
    if (first && assetsDecided) {
      goNext()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, assetsDecided])

  return (
    <div className={styles.fillInformation}>
      {(realName || identity) && (
        <div className={styles.identityCard}>
          <div className={styles.row}>
            <span className={styles.label}>姓名</span>
            <span className={styles.value}>{realName || '-'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>身份证</span>
            <span className={styles.value}>{identity || '-'}</span>
          </div>
        </div>
      )}

      <div className={styles.formCard}>
        <div className={styles.tipBanner}>补充更多信息，通过率提升90%</div>

        <div className={styles.assetSection}>
          <div className={styles.assetHeader} onClick={() => setAssetsExpanded(!assetsExpanded)}>
            <div className={styles.assetTitle}>
              <span>资产(多选)</span>
              <span className={styles.assetSub}>资产<span className={styles.assetEm}>越多</span>，额度<span className={styles.assetEm}>越高</span></span>
            </div>
            <i className={classNames(styles.chevron, assetsExpanded && styles.up)} />
          </div>

          {assetsExpanded && (
            <div className={styles.assetTags}>
              {ASSET_OPTIONS.map(item => {
                const active = !noneOfAbove && assetValues[item.key] === HAS_VALUE
                return (
                  <div
                    key={item.key}
                    className={classNames(styles.tag, active && styles.active)}
                    onClick={() => handleAssetToggle(item.key)}
                  >
                    {item.label}
                  </div>
                )
              })}
              <div
                className={classNames(styles.tag, noneOfAbove && styles.active)}
                onClick={handleNoneOfAbove}
              >
                以上都没有
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.safeTip}>
        <img className={styles.safeIcon} src={require('@imgs/information/wft/safetyIcon.png')} alt="" />
        <span>国家级数据加密保护，仅用于贷款审核</span>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={classNames(styles.nextBtn, !assetsDecided && styles.disabled)}
          onClick={handleNext}
        >
          下一步
        </button>
      </div>

    </div>
  )
}

export default FillInformationLoan
